// ─── Config ──────────────────────────────────────────────────────────────────
const TEDDY_PROMO = {
  teddyCollection: 'teddy-fleece-collection',
  promoCollection: 'teddy-gift-products',
};

// ─── Cache ────────────────────────────────────────────────────────────────────
const _cache = { teddyIds: null, promoVariantIds: null };

async function getTeddyIds() {
  if (_cache.teddyIds) return _cache.teddyIds;
  try {
    const res = await fetch(`/collections/${TEDDY_PROMO.teddyCollection}/products.json?limit=250`);
    if (!res.ok) { console.error('[TeddyPromo] Teddy collection not found'); return []; }
    const { products } = await res.json();
    _cache.teddyIds = products.map(p => p.id);
    return _cache.teddyIds;
  } catch (err) {
    console.error('[TeddyPromo] getTeddyIds:', err);
    return [];
  }
}

async function getPromoVariantIds() {
  if (_cache.promoVariantIds) return _cache.promoVariantIds;
  try {
    const res = await fetch(`/collections/${TEDDY_PROMO.promoCollection}/products.json?limit=250`);
    if (!res.ok) { console.error('[TeddyPromo] Promo collection not found'); return []; }
    const { products } = await res.json();
    _cache.promoVariantIds = products.flatMap(p => p.variants.map(v => v.id));
    return _cache.promoVariantIds;
  } catch (err) {
    console.error('[TeddyPromo] getPromoVariantIds:', err);
    return [];
  }
}

// ─── Cart API ─────────────────────────────────────────────────────────────────
function getCart() {
  return fetch('/cart.js').then(r => r.json());
}

async function waitForPromosRemoved(promoVariantIds, maxAttempts = 10, interval = 300) {
  for (let i = 0; i < maxAttempts; i++) {
    const cart = await getCart();
    const stillHasPromo = cart.items.some(i => promoVariantIds.includes(i.variant_id));
    if (!stillHasPromo) return cart;
    await new Promise(r => setTimeout(r, interval));
  }
  return await getCart();
}

let _internalRequestDepth = 0;

async function cartAdd(variantId) {
  _internalRequestDepth++;
  try {
    return await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    }).then(r => r.json());
  } finally {
    _internalRequestDepth--;
  }
}

async function cartChange(key, quantity) {
  _internalRequestDepth++;
  try {
    return await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then(r => r.json());
  } finally {
    _internalRequestDepth--;
  }
}

// ─── Counter (non-promo only) ─────────────────────────────────────────────────
function updateVisibleCounter(cart, promoVariantIds) {
  const count = cart.items
    .filter(i => !promoVariantIds.includes(i.variant_id))
    .reduce((sum, i) => sum + i.quantity, 0);

  // Update all counter elements (header + drawer)
  document.querySelectorAll('.js-ajax-cart-counter').forEach(counter => {
    counter.textContent = count;
    counter.classList.toggle('hidden', count === 0);
    counter.classList.toggle('visible', count > 0);
  });

  const checkout = document.querySelector('#CartDrawer-Checkout');
  if (checkout) {
    checkout.disabled = count === 0;
    checkout.style.opacity = count === 0 ? '0.4' : '';
    checkout.style.pointerEvents = count === 0 ? 'none' : '';
  }
}

// ─── Drawer refresh ───────────────────────────────────────────────────────────
async function refreshDrawer(promoVariantIds, freshCart = null) {
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  if (!cartDrawerItems) return;

  const cart = freshCart || await getCart();

  const nonPromoCount = cart.items
    .filter(i => !promoVariantIds.includes(i.variant_id))
    .reduce((sum, i) => sum + i.quantity, 0);

  if (nonPromoCount === 0) {
    // Directly set empty state in DOM
    const cartDrawer   = document.querySelector('cart-drawer');
    const itemsWrapper = document.querySelector('.drawer__cart-items-wrapper');
    const drawerFooter = document.querySelector('.drawer__footer');
    const innerEmpty   = document.querySelector('.drawer__inner-empty');

    if (cartDrawer)      cartDrawer.classList.add('is-empty');
    if (cartDrawerItems) cartDrawerItems.classList.add('is-empty');
    if (itemsWrapper)    itemsWrapper.innerHTML = '';
    if (drawerFooter)    drawerFooter.style.display = 'none';
    if (innerEmpty)      innerEmpty.style.display = 'flex';

    updateVisibleCounter(cart, promoVariantIds);
    return;
  }

  try {
    const sectionRes = await fetch(`/?sections=cart-drawer&_=${Date.now()}`).then(async r => {
      if (!r.ok) throw new Error('[TeddyPromo] Failed to load cart-drawer section');
      return r.json();
    });

    if (!sectionRes?.['cart-drawer']) return;

    if (cartDrawerItems.renderCartDrawer) {
      cartDrawerItems.renderCartDrawer({
        item_count: nonPromoCount,
        total_price: cart.total_price,
        sections: { 'cart-drawer': sectionRes['cart-drawer'] },
      });
    }
  } catch (err) {
    console.error('[TeddyPromo] refreshDrawer:', err);
  }

  updateVisibleCounter(cart, promoVariantIds);
}

// ─── Patch renderCartDrawer ───────────────────────────────────────────────────
// Waits for any in-progress syncPromo to finish before allowing drawer render
function patchRenderCartDrawer() {
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  if (!cartDrawerItems?.renderCartDrawer || cartDrawerItems._teddyPatched) return;

  const _orig = cartDrawerItems.renderCartDrawer.bind(cartDrawerItems);

  // ✅ Keep renderCartDrawer synchronous — don't make it async
  cartDrawerItems.renderCartDrawer = function(parsedState) {
    if (_syncing) {
      // Queue the render to run after sync completes
      const check = setInterval(() => {
        if (!_syncing) {
          clearInterval(check);
          _orig(parsedState);
        }
      }, 50);
      return;
    }
    return _orig(parsedState);
  };

  cartDrawerItems._teddyPatched = true;
}

// ─── Checkout lock (blocks native + express/dynamic checkout buttons) ─────────
const CHECKOUT_TEXT_SELECTOR = [
  '#CartDrawer-Checkout',
  '.js-ajax-checkout-button',
  '#checkout',
  'button[name="checkout"]',
].join(', ');

const CHECKOUT_LOCK_SELECTOR = [
  CHECKOUT_TEXT_SELECTOR,
  '.cart__dynamic-checkout-buttons',
  '.additional-checkout-buttons',
].join(', ');

// Reuses the theme's own cart-processing spinner (cart-remove-spinner —

const CHECKOUT_WAIT_HTML = `
  <span style="display:flex;width:100%;align-items:center;justify-content:center;gap:.5em;font-family:inherit;font-size:inherit;font-weight:inherit;">
    <span class="cart-remove-spinner">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    </span>
    <span>Please wait...</span>
  </span>
`;

function lockCheckout() {
  document.querySelectorAll(CHECKOUT_LOCK_SELECTOR).forEach(el => {
    el.style.pointerEvents = 'none';
    el.style.opacity = '0.4';
  });
  // Text buttons get the spinner instead — restore full opacity so it
  // actually reads as "loading" rather than just "disabled".
  document.querySelectorAll(CHECKOUT_TEXT_SELECTOR).forEach(el => {
    if (el.dataset.teddyOrigHtml === undefined) {
      el.dataset.teddyOrigHtml = el.innerHTML;
    }
    el.innerHTML = CHECKOUT_WAIT_HTML;
    el.style.opacity = '';
  });
}

function unlockCheckout() {
  document.querySelectorAll(CHECKOUT_LOCK_SELECTOR).forEach(el => {
    el.style.pointerEvents = '';
    el.style.opacity = '';
  });
  document.querySelectorAll(CHECKOUT_TEXT_SELECTOR).forEach(el => {
    if (el.dataset.teddyOrigHtml !== undefined) {
      el.innerHTML = el.dataset.teddyOrigHtml;
      delete el.dataset.teddyOrigHtml;
    }
  });
}

// ─── Core sync ────────────────────────────────────────────────────────────────
let _syncing      = false;
let _pendingSync  = false;
let _syncPromise  = null;

function syncPromo(lock = true) {
  if (_syncing) {
    _pendingSync = true;
    return _syncPromise;
  }

  _syncing = true;
  if (lock) lockCheckout(); // idempotent — safe even if a chained rerun re-locks an already-locked button
  _syncPromise = _runSyncPromo(lock);
  return _syncPromise;
}

async function _runSyncPromo(lock) {
  try {
    const [teddyIds, promoVariantIds] = await Promise.all([
      getTeddyIds(),
      getPromoVariantIds(),
    ]);

    if (!promoVariantIds.length) { console.warn('[TeddyPromo] no promoVariantIds found — promo collection empty or missing'); return; }

    const cart     = await getCart();
    const hasTeddy = cart.items.some(i => teddyIds.includes(i.product_id));
    let changed    = false;
    let removing   = false;

    console.debug('[TeddyPromo] sync start', {
      teddyIds,
      promoVariantIds,
      hasTeddy,
      cartItems: cart.items.map(i => ({ product_id: i.product_id, variant_id: i.variant_id, key: i.key, qty: i.quantity, title: i.title })),
    });

    for (const variantId of promoVariantIds) {
      const giftItem = cart.items.find(i => i.variant_id === variantId);

      if (hasTeddy) {
        if (!giftItem) {
          console.debug('[TeddyPromo] adding gift', variantId);
          await cartAdd(variantId);
          changed = true;
        } else if (giftItem.quantity !== 1) {
          console.debug('[TeddyPromo] correcting gift qty', variantId);
          await cartChange(giftItem.key, 1);
          changed = true;
        }
      } else {
        if (giftItem) {
          console.debug('[TeddyPromo] removing gift', giftItem.key);
          const result = await cartChange(giftItem.key, 0);
          console.debug('[TeddyPromo] removal result', result);
          changed = true;
          removing = true;
        }
      }
    }

    let freshCart = null;
    if (changed) {
      freshCart = removing
        ? await waitForPromosRemoved(promoVariantIds)
        : await getCart();
      console.debug('[TeddyPromo] freshCart after change', freshCart.items.map(i => ({ variant_id: i.variant_id, qty: i.quantity })));
    }

    await refreshDrawer(promoVariantIds, freshCart);
    console.debug('[TeddyPromo] sync complete');

  } catch (err) {
    console.error('[TeddyPromo]', err);
  } finally {
    _syncing = false;
    if (_pendingSync) {
      _pendingSync = false;
      
      return syncPromo();
    }
    if (lock) unlockCheckout();
  }
}

// ─── Fetch intercept ──────────────────────────────────────────────────────────
(function interceptFetch() {
  const _origFetch = window.fetch;
  window.fetch = function(url, ...args) {
    const urlStr = typeof url === 'string' ? url : (url?.url ?? '');
    const isMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(urlStr);
    if (isMutation && _internalRequestDepth === 0) lockCheckout();
    const promise = _origFetch.call(this, url, ...args);
    if (isMutation && !_syncing && _internalRequestDepth === 0) {
      promise.then(response => { if (response.ok) syncPromo(); else unlockCheckout(); });
    }
    return promise;
  };
})();

// ─── XHR intercept (bundle app) ──────────────────────────────────────────────
(function interceptXHR() {
  const _origOpen = XMLHttpRequest.prototype.open;
  const _origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._teddyUrl    = typeof url === 'string' ? url : '';
    this._teddyMethod = typeof method === 'string' ? method.toUpperCase() : '';
    return _origOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    const isMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(this._teddyUrl);
    const isPost     = this._teddyMethod === 'POST';

    if (isMutation && isPost && _internalRequestDepth === 0) lockCheckout();

    if (isMutation && isPost && !_syncing && _internalRequestDepth === 0) {
      this.addEventListener('load', function() {
        if (this.status >= 200 && this.status < 300) syncPromo();
        else unlockCheckout();
      }, { once: true });
    }

    return _origSend.call(this, ...args);
  };
})();

// ─── Init ─────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  getTeddyIds();
  getPromoVariantIds();

  // Patch renderCartDrawer as soon as element is available
  const waitForDrawer = setInterval(() => {
    const el = document.querySelector('cart-drawer-items');
    if (!el?.renderCartDrawer) return;
    clearInterval(waitForDrawer);
    patchRenderCartDrawer();
  }, 200);

  document.addEventListener('cart:updated', () => syncPromo());
  document.addEventListener('cart:change',  () => syncPromo());

  // Initial sync on page load — silent (no lock/spinner). Nothing has
  // happened yet for the checkout button to race against, so there's no
  // reason to make it visibly wait through this first check.
  syncPromo(false);

  // Checkout guard
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest(
      '#CartDrawer-Checkout, .js-ajax-checkout-button, a[href*="/checkout"], #checkout, button[name="checkout"]'
    );
    if (!btn) return;
    console.debug('[TeddyPromo] checkout click intercepted', btn);
    e.preventDefault();
    e.stopImmediatePropagation();

    await syncPromo();

    
    // the browser had already painted the restored state by then.)
    lockCheckout();

    // Block checkout if only gifts remain
    const promoVariantIds = await getPromoVariantIds();
    const cart = await getCart();
    const nonPromoCount = cart.items
      .filter(i => !promoVariantIds.includes(i.variant_id))
      .reduce((sum, i) => sum + i.quantity, 0);

    console.debug('[TeddyPromo] pre-checkout cart state', cart.items.map(i => ({ variant_id: i.variant_id, qty: i.quantity, title: i.title })), { nonPromoCount });

    if (nonPromoCount === 0) {
      unlockCheckout();
      await refreshDrawer(promoVariantIds, cart);
      return;
    }

    // the page unloads.
    if (btn.tagName === 'BUTTON' && btn.form) {
      btn.form.requestSubmit(btn);
    } else {
      window.location.href = '/checkout/';
    }
  }, true);
});
